// Small building blocks the auth screens share: a failure banner, and a labelled field driven by
// react-hook-form over the kit's `TextField`. The same pattern the console uses
// (apps/console/src/auth/form-controls.tsx).

import { TextField } from '@clavian/ui-web';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';

export function Failure({ message }: { readonly message: string | null }) {
  return message === null ? null : (
    <p className="pw-alert" role="alert">
      {message}
    </p>
  );
}

export interface TextInputProps<TValues extends FieldValues> {
  readonly control: Control<TValues>;
  readonly name: Path<TValues>;
  readonly label: string;
  readonly description?: string;
  readonly type?: string;
  readonly autoComplete?: string;
  readonly inputMode?: 'numeric' | 'tel' | 'email' | 'text';
  /** Focus this field when its step mounts — minimal friction through the sign-in flow. */
  readonly autoFocus?: boolean;
  /** A length cap (the OTP is six digits). */
  readonly maxLength?: number;
  /** What the phone's on-screen Enter key says and does. */
  readonly enterKeyHint?: 'done' | 'go' | 'next' | 'send';
  readonly numeric?: boolean;
  readonly rules: { required: string; validate?: (value: string) => true | string };
}

/** A kit `TextField` driven by react-hook-form, with the rule's message as its error. */
export function TextInput<TValues extends FieldValues>({ control, name, rules, ...input }: TextInputProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules as never}
      render={({ field, fieldState }) => (
        <TextField
          {...input}
          required
          name={field.name}
          value={field.value as string}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}
